import { App, Button, Form, Input, Modal } from 'antd';
import React, { useEffect, useState } from 'react';
import * as authApi from '@/api/auth'
import { useRequest } from 'ahooks';
import { handleLogout } from '@/api/axiosInstance';
import { useTranslation } from 'react-i18next';

interface DeregisterAccountProps {
    open: boolean;
    onClose: () => void;
}

const DeregisterAccount: React.FC<DeregisterAccountProps> = ({ open, onClose }) => {
    const { modal, message } = App.useApp();
    const [form] = Form.useForm<{
        email: string;
        verificationCode: string;
    }>();
    const { t } = useTranslation('common');

    const { loading, run } = useRequest(authApi.deregisterUser, {
        manual: true,
        onError: (error) => {
            console.error('Error delete account:', error);
            message.error(t('deregister.delete_failed'));
        },
        onSuccess: (data) => {
            if (!data?.ok) {
                message.error(data?.message || t('deregister.deregister_failed'));
            } else {
                message.success(t('deregister.delete_success'));
                form.resetFields();
                onClose();
                // Auto logout after successful deregistration
                setTimeout(() => {
                    handleLogout();
                }, 1000);
            }
        }
    })

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();

            // Show confirmation modal before proceeding
            modal.confirm({
                title: t('deregister.confirm_title'),
                content: t('deregister.confirm_content'),
                okText: t('deregister.confirm_delete'),
                okType: 'danger',
                cancelText: t('deregister.cancel'),
                onOk: async () => {
                    await run({
                        email: values.email,
                        verificationCode: values.verificationCode
                    });
                }
            });
        } catch (error) {
            console.error('Validation failed:', error);
        }
    };

    const [countdown, setCountdown] = useState(0);

    useEffect(() => {
        let timer: NodeJS.Timeout;
        if (countdown > 0) {
            timer = setTimeout(() => setCountdown(countdown - 1), 1000);
        }
        return () => clearTimeout(timer);
    }, [countdown]);

    const { loading: sendVerificationLoading, run: runSendVerification } = useRequest(async () => {
        return await authApi.sendVerificationCode(form.getFieldValue('email'), 'deregister');
    }, {
        manual: true,
        onError: (error) => {
            console.error('Error sending verification code:', error);
            message.error(t('deregister.send_code_failed'));
        },
        onSuccess: (data) => {
            if (!data?.ok) {
                message.error(data?.message || t('deregister.send_code_failed'));
            } else {
                message.success(t('deregister.send_code_success'));
                setCountdown(60);
            }
        },
    })

    const handleCancel = () => {
        form.resetFields();
        setCountdown(0);
        onClose();
    };

    return (
        <Modal
            title={t('deregister.title')}
            open={open}
            onCancel={handleCancel}
            onOk={handleSubmit}
            confirmLoading={loading}
            okButtonProps={{ danger: true }}
            okText={t('deregister.ok_text')}
            className="w-[400px]"
        >
            <div className="mb-4 p-3 bg-red-50 dark:bg-[#3C1F1F] border border-red-200 dark:border-[#5C2D2D] rounded-md">
                <p className="text-red-600 dark:text-[#F48771] text-sm">
                    <strong>Warning:</strong> {t('deregister.warning_text')}
                </p>
            </div>

            <Form
                form={form}
                layout="vertical"
                className="mt-4"
                autoComplete="off"
            >
                <Form.Item
                    label={t('deregister.email')}
                    name="email"
                    rules={[
                        { required: true, message: t('deregister.email_required') },
                        { type: 'email', message: t('deregister.email_invalid') }
                    ]}
                >
                    <Input
                        autoComplete="false"
                        className="w-full"
                        placeholder={t('deregister.email_placeholder')}
                    />
                </Form.Item>

                <Form.Item
                    label={t('deregister.verification_code')}
                    name="verificationCode"
                    rules={[
                        { required: true, message: t('deregister.code_required') },
                        { len: 6, message: t('deregister.code_length') }
                    ]}
                >
                    <div className="flex gap-2">
                        <Input
                            autoComplete="off"
                            className="flex-1"
                            placeholder={t('deregister.code_placeholder')}
                            maxLength={6}
                        />
                        <Button
                            type="primary"
                            loading={sendVerificationLoading}
                            disabled={countdown > 0}
                            onClick={runSendVerification}
                            className="whitespace-nowrap"
                        >
                            {countdown > 0 ? `${countdown}s` : t('deregister.get_code')}
                        </Button>
                    </div>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default DeregisterAccount;
